/**
 * Purchase history (Task 21 + Task 22 CSV export).
 * Correct auto-classified categories; flag purchases as recurring; export plaintext CSV.
 */

import { useState } from 'react';
import { formatCentsDisplay } from '../../domain/recommend/explain';
import type { SpendCategory } from '../../domain/recommend/types';
import { spendCategory } from '../../schemas/enums';
import {
  buildPurchaseHistoryCsv,
  downloadPurchaseHistoryCsv,
  type PurchaseHistoryCsvRow,
} from './csvExport';
import type { PurchaseHistoryRow } from './types';

const CATEGORY_OPTIONS = spendCategory.options;

export type PurchaseHistoryScreenProps = {
  rows: readonly PurchaseHistoryRow[];
  onCorrectCategory: (args: {
    transaction_id: string;
    corrected_category: SpendCategory;
  }) => Promise<void>;
  onFlagRecurring: (args: { transaction_id: string }) => Promise<void>;
  /** Override download for tests; defaults to browser file download. */
  onExportCsv?: (csv: string) => void;
};

function toCsvRow(row: PurchaseHistoryRow): PurchaseHistoryCsvRow {
  return {
    date: row.transaction_date,
    merchant_name: row.merchant_name,
    merchant_category: row.category,
    amount_cents: row.amount_cents,
    card_suffix: row.card_suffix,
    expected_reward_cents: row.expected_reward_cents,
    notes: row.notes,
  };
}

function HistoryRow(props: {
  row: PurchaseHistoryRow;
  onCorrectCategory: PurchaseHistoryScreenProps['onCorrectCategory'];
  onFlagRecurring: PurchaseHistoryScreenProps['onFlagRecurring'];
}): JSX.Element {
  const { row } = props;
  const [selected, setSelected] = useState<SpendCategory>(
    row.category !== null && row.category !== 'unknown' ? row.category : 'other',
  );
  const [busy, setBusy] = useState(false);

  return (
    <article
      data-testid="purchase-history-row"
      data-transaction-id={row.transaction_id}
      className="rounded-lg border border-slate-800 bg-slate-900/60 p-4"
    >
      <header className="mb-2 flex items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-medium text-slate-100">{row.merchant_name}</h3>
          {row.description !== null && row.description !== '' ? (
            <p data-testid="purchase-description" className="text-sm text-slate-300">
              {row.description}
            </p>
          ) : null}
          <p className="text-xs text-slate-500">
            {row.transaction_date} · {row.card_label}
          </p>
        </div>
        <span className="text-sm text-slate-100">{formatCentsDisplay(row.amount_cents)}</span>
      </header>

      <p className="text-sm text-slate-300">
        Category: <span data-testid="purchase-category">{row.category ?? 'unknown'}</span>
        <span className="text-xs text-slate-500"> · {row.category_src}</span>
      </p>

      {row.can_correct_category ? (
        <div className="mt-3 flex flex-wrap items-end gap-2">
          <label className="flex flex-col gap-1 text-xs text-slate-400">
            Correct category
            <select
              data-testid="category-correct-select"
              className="rounded border border-slate-700 bg-slate-950 px-2 py-1 text-sm text-slate-100"
              value={selected}
              onChange={(e) => setSelected(e.target.value as SpendCategory)}
              disabled={busy}
            >
              {CATEGORY_OPTIONS.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            data-testid="category-correct-submit"
            className="rounded border border-slate-600 px-3 py-1 text-sm text-slate-100"
            disabled={busy}
            onClick={() => {
              setBusy(true);
              void props
                .onCorrectCategory({
                  transaction_id: row.transaction_id,
                  corrected_category: selected,
                })
                .finally(() => setBusy(false));
            }}
          >
            Save correction
          </button>
        </div>
      ) : null}

      {row.can_flag_recurring ? (
        <button
          type="button"
          data-testid="flag-recurring-submit"
          className="mt-3 rounded border border-slate-600 px-3 py-1 text-sm text-slate-100"
          disabled={busy}
          onClick={() => {
            setBusy(true);
            void props
              .onFlagRecurring({ transaction_id: row.transaction_id })
              .finally(() => setBusy(false));
          }}
        >
          Flag as recurring
        </button>
      ) : (
        <p className="mt-3 text-xs text-emerald-400/90" data-testid="recurring-flagged">
          Flagged as recurring
        </p>
      )}
    </article>
  );
}

export function PurchaseHistoryScreen({
  rows,
  onCorrectCategory,
  onFlagRecurring,
  onExportCsv,
}: PurchaseHistoryScreenProps): JSX.Element {
  return (
    <section data-testid="purchase-history-screen" className="flex flex-col gap-4">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-medium text-slate-100">Purchase history</h2>
          <p className="mt-1 text-sm text-slate-400">
            Correct auto-classified categories and flag recurring charges. Corrections write
            merchant category observations for future recommendations.
          </p>
        </div>
        <button
          type="button"
          data-testid="purchase-history-export"
          className="rounded border border-slate-600 px-3 py-1 text-sm text-slate-100 disabled:opacity-40"
          disabled={rows.length === 0}
          onClick={() => {
            const csv = buildPurchaseHistoryCsv(rows.map(toCsvRow));
            if (onExportCsv) {
              onExportCsv(csv);
            } else {
              downloadPurchaseHistoryCsv(csv);
            }
          }}
        >
          Export
        </button>
      </header>

      {rows.length === 0 ? (
        <p className="text-sm text-slate-400">No confirmed purchases yet.</p>
      ) : (
        rows.map((row) => (
          <HistoryRow
            key={row.transaction_id}
            row={row}
            onCorrectCategory={onCorrectCategory}
            onFlagRecurring={onFlagRecurring}
          />
        ))
      )}
    </section>
  );
}
